import {
  COMMENT_REQUIRED_FROM_HOURS,
  MAX_ENTRY_HOURS,
  MIN_ENTRY_HOURS,
} from "@/lib/time/constants";
import { type IsoDate, isAfterCurrentWeek } from "@/lib/time/dates";

/**
 * The time-entry save rules (spec "Rules" 2-11; Vinyl TE-26..TE-32 with the 2026-10-15
 * decisions). Pure: the server runs them before every write for a clear message, the UI runs
 * them live. The gateway SQL repeats every block in its WHERE and is the authority.
 */

export type BlockCode =
  | "not-assignable"
  | "no-period"
  | "month-closed"
  | "period-signed-off"
  | "hours-range"
  | "hours-step"
  | "comment-required"
  | "no-guidance"
  | "over-threshold";

export type WarningCode = "over-guidance" | "hours-limit" | "after-this-week";

interface RuleMessage<C extends string> {
  code: C;
  text: string;
}

/** What the rules need to know about the entry's task, assignment and month. */
export interface SaveContext {
  isAssignable: boolean;
  projectIsBillable: boolean;
  /**
   * The assignment's demand has a non-billable billing type (BillingType.IsNonBillable), so
   * its entries are typed Internal. Exempt from the no-plan block only (Zack, 2026-10-15).
   */
  isInternal: boolean;
  guidance: number | null;
  /** Other active hours on this assignment in the entry's month (never the edited entry). */
  monthLogged: number;
  thresholdPct: number;
  monthPeriodFound: boolean;
  monthClosed: boolean;
  periodSignedOff: boolean;
  hoursLimit: number | null;
  /** Every person's active hours on the task, all time, minus the edited entry. */
  taskLogged: number;
  /** The edited entry's stored hours; undefined for a new entry. */
  previousHours?: number;
}

export interface SaveInput {
  date: IsoDate;
  hours: number;
  comment: string | null;
}

export interface SaveVerdict {
  blocks: RuleMessage<BlockCode>[];
  warnings: RuleMessage<WarningCode>[];
}

const EPSILON = 1e-9;

const NO_PLAN_TEXT = "No guidance this month. Won't save.";

function overThresholdText(thresholdPct: number): string {
  return `Over guidance by more than ${Math.round(thresholdPct * 100)}%. Won't save.`;
}

/** "1 hr over guidance", "2 hrs over guidance": singular only for exactly one hour. */
export function overPlanText(over: number): string {
  const text = formatHours(over);
  return `${text} ${text === "1" ? "hr" : "hrs"} over guidance`;
}

export function formatHours(hours: number): string {
  return String(Math.round(hours * 100) / 100);
}

export function isQuarterHour(hours: number): boolean {
  return Math.abs(hours * 4 - Math.round(hours * 4)) < EPSILON;
}

export function normalizeComment(comment: string | null | undefined): string | null {
  const trimmed = comment?.trim() ?? "";
  return trimmed === "" ? null : trimmed;
}

/**
 * TE-32, widened (DECISIONS 2026-10-28): an entry of 2 hours or more is never saved without a
 * comment, billable or internal. Applies to every new save and edit; entries already stored
 * are judged by the grid's `isNotSaved` (src/lib/time/grid.ts), the rule they were saved under.
 */
export function needsComment(hours: number, comment: string | null | undefined): boolean {
  return hours >= COMMENT_REQUIRED_FROM_HOURS && normalizeComment(comment) === null;
}

export function evaluateSave(ctx: SaveContext, input: SaveInput, today: IsoDate): SaveVerdict {
  const blocks: RuleMessage<BlockCode>[] = [];
  const warnings: RuleMessage<WarningCode>[] = [];
  const block = (code: BlockCode, text: string) => blocks.push({ code, text });

  if (!ctx.isAssignable) block("not-assignable", "Not assigned to this task");
  if (!ctx.monthPeriodFound) block("no-period", "No period for this date");
  else if (ctx.monthClosed) block("month-closed", "Month closed");
  if (ctx.periodSignedOff) block("period-signed-off", "Signed off");

  const hoursInRange = input.hours >= MIN_ENTRY_HOURS && input.hours <= MAX_ENTRY_HOURS;
  if (!hoursInRange) block("hours-range", "Hours must be 0.25 to 24");
  else if (!isQuarterHour(input.hours)) block("hours-step", "Use quarter hours");

  if (needsComment(input.hours, input.comment)) {
    block("comment-required", "Needs a comment to save");
  }

  // Controller decision (2026-10-15): an edit that does not add hours may pass the guidance
  // blocks, so someone can still fix or comment an entry on an over-plan assignment.
  const notAddingHours =
    hoursInRange && ctx.previousHours !== undefined && input.hours <= ctx.previousHours + EPSILON;

  const guidance = ctx.guidance ?? 0;
  const total = ctx.monthLogged + (hoursInRange ? input.hours : 0);
  let overThreshold = false;
  if (ctx.projectIsBillable && !notAddingHours) {
    if (guidance <= 0) {
      if (!ctx.isInternal) block("no-guidance", NO_PLAN_TEXT);
    } else if (total > guidance * (1 + ctx.thresholdPct) + EPSILON) {
      overThreshold = true;
      block("over-threshold", overThresholdText(ctx.thresholdPct));
    }
  }

  if (guidance > 0 && total > guidance + EPSILON && !overThreshold) {
    warnings.push({
      code: "over-guidance",
      text: overPlanText(total - guidance),
    });
  }
  if (
    ctx.hoursLimit !== null &&
    hoursInRange &&
    ctx.taskLogged + input.hours > ctx.hoursLimit + EPSILON
  ) {
    warnings.push({ code: "hours-limit", text: "Over the task limit" });
  }
  if (isAfterCurrentWeek(input.date, today)) {
    warnings.push({ code: "after-this-week", text: "Date is after this week" });
  }
  return { blocks, warnings };
}

export type LockReason = "pto" | "invoiced" | "signed-off" | "month-closed";

/** Short label in the grid and Entries list. */
export const LOCK_LABELS: Record<LockReason, string> = {
  pto: "From PTO",
  invoiced: "Invoiced",
  "signed-off": "Signed off",
  "month-closed": "Month closed",
};

/** What the server answers when someone tries to change a locked entry. */
export const LOCK_MESSAGES: Record<LockReason, string> = {
  pto: "PTO time changes on the PTO page",
  invoiced: "Invoiced. Can't change.",
  "signed-off": "Signed off",
  "month-closed": "Month closed",
};

export interface LockableEntry {
  isPto: boolean;
  isInvoiced: boolean;
  periodSignedOff: boolean;
  monthClosed: boolean;
}

/** Rules 8 and 9: PTO, invoiced, signed-off and closed-month entries are read-only here. */
export function entryLock(entry: LockableEntry): LockReason | null {
  if (entry.isPto) return "pto";
  if (entry.isInvoiced) return "invoiced";
  if (entry.periodSignedOff) return "signed-off";
  if (entry.monthClosed) return "month-closed";
  return null;
}

type PlanState = "none" | "no-plan" | "ok" | "over" | "blocked";

export interface PlanStatus {
  state: PlanState;
  /** "9 of 40 hrs", or null when there is no guidance. */
  label: string | null;
  /** 0..1 for the bar. */
  ratio: number;
  /** The status chip, or null when the row needs none. */
  text: string | null;
}

export function planStatus(args: {
  guidance: number | null;
  logged: number;
  thresholdPct: number;
  projectIsBillable: boolean;
  isInternal: boolean;
}): PlanStatus {
  const guidance = args.guidance ?? 0;
  if (guidance <= 0) {
    return args.projectIsBillable && !args.isInternal
      ? // No standing alert (UI standard 7): the row shows a muted "No guidance" and stays
        // editable; NO_PLAN_TEXT appears only when a value is held for it.
        { state: "no-plan", label: null, ratio: 0, text: null }
      : { state: "none", label: null, ratio: 0, text: null };
  }
  const label = `${formatHours(args.logged)} of ${formatHours(guidance)} hrs`;
  const ratio = Math.min(args.logged / guidance, 1);
  if (args.projectIsBillable && args.logged > guidance * (1 + args.thresholdPct) + EPSILON) {
    return { state: "blocked", label, ratio, text: overThresholdText(args.thresholdPct) };
  }
  if (args.logged > guidance + EPSILON) {
    return {
      state: "over",
      label,
      ratio,
      text: overPlanText(args.logged - guidance),
    };
  }
  return { state: "ok", label, ratio, text: null };
}
